import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  LPSettlementBatchMessage,
  LPSettlementBatchSubscription,
  LPSettlementInstructionMessage,
  LPSettlementInstructionSubscription,
} from '@src/containers/Portfolio/LpSettlement/generated/lpSettlementTypes';
import type {
  CareOrder,
  CustomerBalance,
  CustomerSpecificSecurity,
  ExecutionReport,
  MarketSecurityStatus,
  Order,
  PositionUpdateSourceEnum,
  ProductTypeEnum,
  Quote,
  RequestStream,
} from '@talos/kyoko';
import {
  CARE_EXECUTION_REPORT,
  CARE_ORDER,
  CareExecutionReport,
  COUNTERPARTY,
  CUSTOMER_QUOTE,
  CUSTOMER_SECURITY,
  type CustomerQuote,
  DEFAULT_BIGGER_PAGINATION_SIZE,
  FIXING_INDEX_MARK,
  type FixingIndexMarkMessageData,
  MARKET_ACCOUNT_DETAILS,
  POSITION,
  type Position,
  SECURITY,
  type Security,
  WORKING_EXECUTION_REPORT,
  WORKING_ORDER,
} from '@talos/kyoko';
import type { MarketAccountDetailsMessageData } from '../../containers/Admin/Counterparties/CounterpartyTypes';
import type { TCounterparty } from '../../containers/Admin/Counterparties/types';
import {
  addCounterpartyToCache,
  getCounterpartyCacheKey,
  normalizeCounterpartiesForCache,
} from './counterpartyCacheUtils';
import { noopBaseQuery } from './noopBaseQuery';
import { buildWsEndpointBuilder } from './wsEndpointBuilder';

export type BaseWsQuery<Params = { [key: string]: any }> = Omit<RequestStream, 'name'> & Params;

export const streamingDataSlice = createApi({
  reducerPath: 'streamingData',
  baseQuery: noopBaseQuery,
  keepUnusedDataFor: 30,
  endpoints: builder => {
    const wsBuilder = buildWsEndpointBuilder<typeof builder>(builder);
    const buildParentOrderEndpoint = (streamName: typeof CARE_ORDER | typeof WORKING_ORDER) =>
      wsBuilder.query<Map<string, CareOrder>, BaseWsQuery>({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({ name: streamName, tag, ...args }),
        initializeCache: (data: CareOrder[]) => data.reduce((map, order) => map.set(order.OrderID, order), new Map()),
        updateCache: (draft, data: CareOrder[]) =>
          new Map(data.reduce((map, order) => map.set(order.OrderID, order), draft)),
      });
    const buildCareExecutionReportsEndpoint = (
      streamName: typeof CARE_EXECUTION_REPORT | typeof WORKING_EXECUTION_REPORT
    ) =>
      wsBuilder.query<Map<string, CareExecutionReport>, BaseWsQuery>({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({ name: streamName, tag, ...args }),
        initializeCache: (data: CareExecutionReport[]) =>
          data.reduce((map, report) => map.set(report.ExecID, new CareExecutionReport(report)), new Map()),
        updateCache: (draft, data: CareExecutionReport[]) =>
          new Map(data.reduce((map, report) => map.set(report.ExecID, new CareExecutionReport(report)), draft)),
      });
    return {
      getOrders: wsBuilder.query<Map<string, Order>, BaseWsQuery>({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({ name: 'Order', tag, ...args }),
        initializeCache: (data: Order[]) => data.reduce((map, order) => map.set(order.OrderID, order), new Map()),
        updateCache: (draft, data: Order[]) =>
          new Map(data.reduce((map, order) => map.set(order.OrderID, order), draft)),
      }),
      getExecutionReports: wsBuilder.query<Map<string, ExecutionReport>, BaseWsQuery>({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({ name: 'ExecutionReport', tag, ...args }),
        initializeCache: (data: ExecutionReport[]) =>
          data.reduce((map, report) => map.set(report.ExecID, report), new Map()),
        updateCache: (draft, data: ExecutionReport[]) =>
          new Map(data.reduce((map, report) => map.set(report.ExecID, report), draft)),
      }),
      getQuotes: wsBuilder.query<Map<string, Quote>, BaseWsQuery>({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({ name: 'Quote', tag, ...args }),
        initializeCache: (data: Quote[]) => data.reduce((map, quote) => map.set(quote.RFQID, quote), new Map()),
        updateCache: (draft, data: Quote[]) => new Map(data.reduce((map, quote) => map.set(quote.RFQID, quote), draft)),
      }),
      getCareOrders: buildParentOrderEndpoint(CARE_ORDER),
      getWorkingOrders: buildParentOrderEndpoint(WORKING_ORDER),
      getCustomerQuotes: wsBuilder.query<Map<string, CustomerQuote>, BaseWsQuery>({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({ name: CUSTOMER_QUOTE, tag, ...args }),
        initializeCache: (data: CustomerQuote[]) => data.reduce((map, quote) => map.set(quote.RFQID, quote), new Map()),
        updateCache: (draft, data: CustomerQuote[]) =>
          new Map(data.reduce((map, quote) => map.set(quote.RFQID, quote), draft)),
      }),
      getCareExecutionReports: buildCareExecutionReportsEndpoint(CARE_EXECUTION_REPORT),
      getWorkingOrderExecutionReports: buildCareExecutionReportsEndpoint(WORKING_EXECUTION_REPORT),
      getCustomerBalance: wsBuilder.query<
        Map<string, CustomerBalance>,
        BaseWsQuery & { Counterparties?: string[]; Symbols?: string[]; Currencies?: string[] }
      >({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({ name: 'CustomerBalance', tag, ...args }),
        initializeCache: (data: CustomerBalance[]) => data.reduce((map, item) => map.set(item.rowID, item), new Map()),
        updateCache: (draft, data: CustomerBalance[]) => data.reduce((map, item) => map.set(item.rowID, item), draft),
      }),
      getMarketSecurityStatuses: wsBuilder.query<
        Map<string, Map<string, MarketSecurityStatus>>,
        BaseWsQuery & { Symbols?: string[] }
      >({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({
          name: 'MarketSecurityStatus',
          Throttle: '1s',
          tag,
          ...args,
        }),
        initializeCache: (data: MarketSecurityStatus[]) => {
          return data.reduce((outerMap, status) => {
            const innerMap = outerMap.get(status.MarketAccount) ?? new Map();
            innerMap.set(status.Symbol, status);
            outerMap.set(status.MarketAccount, innerMap);
            return outerMap;
          }, new Map<string, Map<string, MarketSecurityStatus>>());
        },

        updateCache: (draft, data: MarketSecurityStatus[]) => {
          for (const status of data) {
            const innerMap = draft.get(status.MarketAccount) ?? new Map();
            innerMap.set(status.Symbol, status);
            draft.set(status.MarketAccount, innerMap);
          }
          return new Map(draft); // create a new instance each update to trigger change detection both in redux and react
        },
      }),
      getPositions: wsBuilder.query<
        Map<string, Position>,
        BaseWsQuery & { AssetTypes?: ProductTypeEnum[]; Source?: PositionUpdateSourceEnum /* You can add more here */ }
      >({
        /** Hard-coding Throttle 3s for now. If that doesn't fit you you can parameterize it */
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({ name: POSITION, tag, Throttle: '3s', ...args }),
        initializeCache: (data: Position[]) => data.reduce((map, item) => map.set(item.rowID, item), new Map()),
        updateCache: (draft, data: Position[]) => data.reduce((map, item) => map.set(item.rowID, item), draft),
      }),
      getCounterparties: wsBuilder.query<Map<string, TCounterparty>, BaseWsQuery>({
        buildRequest: ({ tag = 'streamingDataSlice', limit = DEFAULT_BIGGER_PAGINATION_SIZE, ...args }) => ({
          name: COUNTERPARTY,
          tag,
          limit,
          ...args,
        }),
        initializeCache: (data: TCounterparty[]) =>
          normalizeCounterpartiesForCache(data).reduce(addCounterpartyToCache, new Map<string, TCounterparty>()),
        updateCache: (draft, data: TCounterparty[]) => {
          for (const item of normalizeCounterpartiesForCache(data)) {
            addCounterpartyToCache(draft, item);
          }
          return new Map(draft); // create a new instance each update to trigger change detection both in redux and react
        },
        removeSupport: {
          getUniqueKey: item => getCounterpartyCacheKey(item),
        },
      }),
      getMarketAccountDetails: wsBuilder.query<Map<string, MarketAccountDetailsMessageData>, BaseWsQuery>({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({ name: MARKET_ACCOUNT_DETAILS, tag, ...args }),
        initializeCache: (data: MarketAccountDetailsMessageData[]) => {
          return data.reduce((map, item) => {
            map.set(item.MarketAccount, item);
            return map;
          }, new Map());
        },
        updateCache: (draft, data: MarketAccountDetailsMessageData[]) => {
          for (const item of data) {
            draft.set(item.MarketAccount, item);
          }
          return new Map(draft); // create a new instance each update to trigger change detection both in redux and react
        },
        removeSupport: {
          getUniqueKey: (item: MarketAccountDetailsMessageData) => item.MarketAccount,
        },
      }),
      getCustomerSecurities: wsBuilder.query<
        Map<string, CustomerSpecificSecurity>,
        BaseWsQuery<GetCustomerSecuritiesRequestParams>
      >({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({ name: CUSTOMER_SECURITY, tag, ...args }),
        initializeCache: (data: CustomerSpecificSecurity[]) =>
          data.reduce((map, item) => map.set(item.Symbol, item), new Map()),
        updateCache: (draft, data: CustomerSpecificSecurity[]) =>
          data.reduce((map, item) => map.set(item.Symbol, item), draft),
        removeSupport: {
          getUniqueKey: item => item.Symbol,
        },
      }),
      getSecurities: wsBuilder.query<Map<string, Security>, BaseWsQuery & { Symbols?: string[] }>({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({ name: SECURITY, tag, ...args }),
        initializeCache: (data: Security[]) => data.reduce((map, item) => map.set(item.Symbol, item), new Map()),
        updateCache: (draft, data: Security[]) => data.reduce((map, item) => map.set(item.Symbol, item), draft),
      }),
      lpSettlementBatch: wsBuilder.query<
        Map<string, LPSettlementBatchMessage['data'][0]>,
        BaseWsQuery<Omit<LPSettlementBatchSubscription, 'name'>>
      >({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({
          name: 'LPSettlementBatch',
          tag,
          ...args,
        }),
        initializeCache: (data: LPSettlementBatchMessage['data'][0][]) =>
          data.reduce((map, item) => map.set(item.BatchID, item), new Map()),
        updateCache: (draft, data: LPSettlementBatchMessage['data'][0][]) =>
          data.reduce((map, item) => map.set(item.BatchID, item), draft),
      }),
      lpSettlementInstructions: wsBuilder.query<
        Map<string, LPSettlementInstructionMessage['data'][0]>,
        BaseWsQuery<Omit<LPSettlementInstructionSubscription, 'name'>>
      >({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({
          name: 'LPSettlementInstruction',
          tag,
          ...args,
        }),
        initializeCache: (data: LPSettlementInstructionMessage['data'][0][]) =>
          data.reduce((map, item) => map.set(item.InstructionID, item), new Map()),
        updateCache: (draft, data: LPSettlementInstructionMessage['data'][0][]) =>
          data.reduce((map, item) => map.set(item.InstructionID, item), draft),
      }),
      getFixingIndexMarks: wsBuilder.query<Map<string, FixingIndexMarkMessageData>, BaseWsQuery>({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => ({ name: FIXING_INDEX_MARK, tag, ...args }),
        initializeCache: (data: FixingIndexMarkMessageData[]) =>
          data.reduce((map, item) => (item.Index ? map.set(item.Index, item) : map), new Map()),
        updateCache: (draft, data: FixingIndexMarkMessageData[]) =>
          data.reduce((map, item) => (item.Index ? map.set(item.Index, item) : map), draft),
        removeSupport: {
          getUniqueKey: (item: FixingIndexMarkMessageData) => item.Index,
        },
      }),
    };
  },
});

export type GetCustomerSecuritiesRequestParams = { Counterparty: string };
export const isCustomerSecuritiesRequestParams = (
  params: Partial<GetCustomerSecuritiesRequestParams>
): params is GetCustomerSecuritiesRequestParams => {
  return params && params?.Counterparty != null;
};

export const {
  useGetCareOrdersQuery,
  useLazyGetCareOrdersQuery,
  useGetWorkingOrdersQuery,
  useLazyGetWorkingOrdersQuery,
  useGetOrdersQuery,
  useGetExecutionReportsQuery,
  useGetQuotesQuery,
  useGetCareExecutionReportsQuery,
  useGetWorkingOrderExecutionReportsQuery,
  useGetCustomerBalanceQuery,
  useGetCounterpartiesQuery,
  useGetFixingIndexMarksQuery,
  useGetMarketAccountDetailsQuery,
  useGetCustomerSecuritiesQuery,
  useLpSettlementBatchQuery,
  useLpSettlementInstructionsQuery,
  useGetCustomerQuotesQuery,
} = streamingDataSlice;
